import React, { useEffect, useState } from "react";
import { SoundToggle } from "@/components/SoundToggle";
import { Accessibility, Type, Contrast, BookOpen, Wind, Link2, Captions, Music2, Bell, Activity, Volume2, Mic, RotateCcw, Square } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { useA11y, speak, stopSpeaking } from "@/context/A11yContext";

const Row = ({ icon: Icon, label, hint, checked, onChange, id }) => (
  <label htmlFor={id} className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-card p-3">
    <Icon size={18} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
    <span className="min-w-0 flex-1"><span className="block text-sm font-medium text-foreground">{label}</span><span className="block text-xs text-muted-foreground">{hint}</span></span>
    <Switch id={id} checked={checked} onCheckedChange={onChange} data-testid={id} />
  </label>
);

const Group = ({ title, children }) => (
  <section className="mt-6">
    <h3 className="font-mono text-[11px] uppercase tracking-widest text-primary">{title}</h3>
    <div className="mt-2 space-y-2">{children}</div>
  </section>
);

export const A11yPanel = () => {
  const a = useA11y();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const fn = () => setOpen(true);
    window.addEventListener("jt-open-a11y", fn);
    return () => window.removeEventListener("jt-open-a11y", fn);
  }, []);
  const readPage = () => {
    const main = document.getElementById("main-content");
    speak((main?.innerText || document.body.innerText).slice(0, 12000), a.rate);
  };
  return (
    <>
      {!open && <button onClick={() => setOpen(true)} data-testid="a11y-tab" aria-label="Accessibility settings: appearance and sound"
        className="fixed bottom-5 left-5 z-[110] flex items-center gap-1.5 rounded-full border-2 border-primary bg-card px-3.5 py-2.5 text-[13px] font-semibold text-foreground shadow-xl transition-transform hover:scale-105 print:hidden">
        <Accessibility size={18} className="text-primary" aria-hidden="true" /> <span className="hidden sm:inline">Accessibility</span>
      </button>}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[92vw] overflow-y-auto sm:max-w-md" data-testid="a11y-panel">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><Accessibility size={20} className="text-primary" /> Accessibility</SheetTitle>
            <SheetDescription>Change how the site looks and sounds. Your choices are saved on this device.</SheetDescription>
          </SheetHeader>

          <Group title="Appearance">
            <div className="rounded-xl border border-border bg-card p-3">
              <p className="flex items-center gap-2 text-sm font-medium"><Type size={18} className="text-primary" aria-hidden="true" /> Text size</p>
              <div className="mt-2 grid grid-cols-4 gap-1.5" role="radiogroup" aria-label="Text size">
                {["A", "A+", "A++", "A+++"].map((l, i) => (
                  <button key={l} role="radio" aria-checked={a.textSize === i} onClick={() => a.set({ textSize: i })} data-testid={`a11y-text-${i}`}
                    className={`rounded-lg border py-1.5 text-sm transition-colors ${a.textSize === i ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>{l}</button>
                ))}
              </div>
            </div>
            <Row id="a11y-contrast" icon={Contrast} label="High contrast" hint="Black background, white text, bright yellow highlights" checked={a.contrast} onChange={(v) => a.set({ contrast: v })} />
            <Row id="a11y-dyslexia" icon={BookOpen} label="Dyslexia-friendly text" hint="Clearer letter shapes and wider spacing" checked={a.dyslexia} onChange={(v) => a.set({ dyslexia: v })} />
            <Row id="a11y-motion" icon={Wind} label="Reduce motion" hint="Stops floating notes and animations" checked={a.reduceMotion} onChange={(v) => a.set({ reduceMotion: v })} />
            <Row id="a11y-links" icon={Link2} label="Highlight links & focus" hint="Underlines links and shows a bold outline when using a keyboard" checked={a.links} onChange={(v) => a.set({ links: v })} />
          </Group>

          <Group title="Sound & hearing">
            <Row id="a11y-captions" icon={Captions} label="Video captions" hint="Show subtitles on course videos" checked={a.captions} onChange={(v) => a.set({ captions: v })} />
            <Row id="a11y-lyrics" icon={Music2} label="Song lyrics on performance videos" hint="Show auto-generated lyrics on Jason's live performance videos" checked={a.lyrics} onChange={(v) => a.set({ lyrics: v })} />
            <Row id="a11y-notices" icon={Bell} label="Show sounds as text" hint="A caption appears whenever a note, scale or track plays" checked={a.notices} onChange={(v) => a.set({ notices: v })} />
            <div className="flex items-center justify-between rounded-xl border border-border bg-card p-3"><p className="text-sm font-medium">Right/wrong sounds in games & quizzes</p><SoundToggle testId="a11y-feedback-sounds" /></div>
            <Row id="a11y-beat" icon={Activity} label="Visual beat flash" hint="A light flashes on every beat of the metronome and play-alongs (red = beat 1)" checked={a.beatFlash} onChange={(v) => a.set({ beatFlash: v })} />
            <div className="rounded-xl border border-border bg-card p-3">
              <p className="flex items-center gap-2 text-sm font-medium"><Volume2 size={18} className="text-primary" aria-hidden="true" /> Volume of notes & tracks: {Math.round(a.volume * 100)}%</p>
              <Slider className="mt-3" value={[a.volume * 100]} min={0} max={150} step={5} onValueChange={([v]) => a.set({ volume: v / 100 })} aria-label="Volume" data-testid="a11y-volume" />
            </div>
          </Group>

          <Group title="Read aloud">
            <div className="rounded-xl border border-border bg-card p-3">
              <p className="flex items-center gap-2 text-sm font-medium"><Mic size={18} className="text-primary" aria-hidden="true" /> Reading speed: {a.rate.toFixed(1)}×</p>
              <Slider className="mt-3" value={[a.rate * 10]} min={6} max={15} step={1} onValueChange={([v]) => a.set({ rate: v / 10 })} aria-label="Reading speed" data-testid="a11y-rate" />
              <div className="mt-3 flex gap-2">
                <Button size="sm" className="rounded-full" onClick={readPage} data-testid="a11y-read-page"><Mic size={14} className="mr-1.5" /> Read this page</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={stopSpeaking} data-testid="a11y-read-stop"><Square size={13} className="mr-1.5" /> Stop</Button>
              </div>
            </div>
          </Group>

          <Button variant="ghost" className="mt-6 w-full rounded-full" onClick={a.reset} data-testid="a11y-reset"><RotateCcw size={14} className="mr-1.5" /> Reset to default</Button>
          <p className="mt-3 text-base text-foreground" data-testid="a11y-theme-tip">Tip: the sun/moon button at the top switches light and dark mode. Keyboard users can press Tab to move and Enter to play any note.</p>
        </SheetContent>
      </Sheet>
    </>
  );
};
